import { Link, useLocation } from 'react-router-dom'
import { generateBreadcrumbStructuredData } from '../lib/structured-data-utils'
import { StructuredData } from '../lib/structured-data.tsx'

interface BreadcrumbsProps {
  customCrumbs?: Array<{ name: string; path: string }>
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ customCrumbs }) => {
  const location = useLocation()
  const pathname = location.pathname
  const searchParams = new URLSearchParams(location.search)

  const generateCrumbs = () => {
    const crumbs = [{ name: 'Home', path: '/' }]

    if (customCrumbs) {
      return [...crumbs, ...customCrumbs]
    }

    // Auto-generate based on path
    const segments = pathname.split('/').filter(Boolean)
    let currentPath = ''

    segments.forEach((segment) => {
      currentPath += `/${segment}`

      // Handle special cases
      if (segment === 'services') {
        const serviceParam = searchParams.get('service')
        crumbs.push({ name: 'Services', path: '/services' })

        if (serviceParam) {
          const serviceTitles: Record<string, string> = {
            development: 'Development',
            cloud: 'Cloud Solutions',
            consulting: 'IT Consulting',
            support: 'Support & Maintenance',
            ai: 'AI',
            web3: 'Web3 & Blockchain'
          }

          if (serviceTitles[serviceParam]) {
            crumbs.push({
              name: serviceTitles[serviceParam],
              path: `/services?service=${serviceParam}`
            })
          }
        }
      } else {
        // Capitalize segment for display
        const title = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ')
        crumbs.push({ name: title, path: currentPath })
      }
    })

    return crumbs
  }

  const breadcrumbs = generateCrumbs()

  // Don't show on homepage
  if (breadcrumbs.length <= 1) return null

  const structuredData = generateBreadcrumbStructuredData(breadcrumbs)

  return (
    <>
      <StructuredData data={structuredData} />
      <nav aria-label="Breadcrumb" className="container-custom py-4">
        <ol className="flex items-center space-x-2 text-sm">
          {breadcrumbs.map((crumb, index) => (
            <li key={crumb.path} className="flex items-center">
              {index > 0 && (
                <svg className="w-4 h-4 mx-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              )}
              {index === breadcrumbs.length - 1 ? (
                <span className="text-gray-500 dark:text-gray-400">{crumb.name}</span>
              ) : (
                <Link
                  to={crumb.path}
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                >
                  {crumb.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </nav>
    </>
  )
}

export default Breadcrumbs